import * as THREE from 'three';

export default function Backdrop({
  hexColor = '#42aad7',
  useBackgroundGradient,
}: {
  hexColor?: string;
  useBackgroundGradient?: boolean;
}) {
  return (
    <>
      {useBackgroundGradient ? (
        <>
          <BackdropGradient hexColor={hexColor} planePosition="top" />
          <BackdropGradient hexColor={hexColor} planePosition="bottom" />
          <BackdropGradient hexColor={hexColor} planePosition="front" />
          <BackdropGradient hexColor={hexColor} planePosition="back" />
          <BackdropGradient hexColor={hexColor} planePosition="left" />
          <BackdropGradient hexColor={hexColor} planePosition="right" />
        </>
      ) : (
        <>
          <BackdropSolid hexColor={hexColor} planePosition="top" />
          <BackdropSolid hexColor={hexColor} planePosition="bottom" />
          <BackdropSolid hexColor={hexColor} planePosition="front" />
          <BackdropSolid hexColor={hexColor} planePosition="back" />
          <BackdropSolid hexColor={hexColor} planePosition="left" />
          <BackdropSolid hexColor={hexColor} planePosition="right" />
        </>
      )}
    </>
  );
}

function BackdropSolid({
  hexColor,
  planePosition,
}: {
  hexColor: string;
  planePosition: 'top' | 'bottom' | 'front' | 'back' | 'left' | 'right';
}) {
  let rot: THREE.Euler;
  let pos: THREE.Vector3;
  switch (planePosition) {
    case 'top':
      rot = new THREE.Euler(Math.PI / 2, 0, 0);
      pos = new THREE.Vector3(0, 500, 0);
      break;
    case 'bottom':
      rot = new THREE.Euler(-Math.PI / 2, 0, 0);
      pos = new THREE.Vector3(0, -500, 0);
      break;
    case 'back':
      rot = new THREE.Euler(0, 0, 0);
      pos = new THREE.Vector3(0, 0, -500);
      break;
    case 'front':
      rot = new THREE.Euler(0, Math.PI, 0);
      pos = new THREE.Vector3(0, 0, 500);
      break;
    case 'left':
      rot = new THREE.Euler(0, Math.PI / 2, 0);
      pos = new THREE.Vector3(-500, 0, 0);
      break;
    case 'right':
      rot = new THREE.Euler(0, -Math.PI / 2, 0);
      pos = new THREE.Vector3(500, 0, 0);
      break;
    default:
      rot = new THREE.Euler(-Math.PI / 2, 0, 0);
      pos = new THREE.Vector3(0, 0, -500);
  }
  return (
    <mesh
      rotation={rot}
      position={pos}
      receiveShadow={false}
      castShadow={false}
    >
      <planeGeometry attach="geometry" args={[2000, 2000]} />
      <meshBasicMaterial
        attach="material"
        color={new THREE.Color(hexColor)}
        side={THREE.FrontSide}
        toneMapped={false}
        depthWrite={false}
        fog={false}
      />
    </mesh>
  );
}

function BackdropGradient({
  hexColor,
  planePosition,
}: {
  hexColor: string;
  planePosition: 'top' | 'bottom' | 'front' | 'back' | 'left' | 'right';
}) {
  const radius = 500;
  const floorColor = '#EFEFEF';
  const gradientTexture = gradTexture([
    [1, 0.7, 0.42, 0],
    [floorColor, floorColor, hexColor, hexColor],
  ]);

  let rot: THREE.Euler;
  let pos: THREE.Vector3;
  let map: THREE.Texture;
  switch (planePosition) {
    case 'top':
      rot = new THREE.Euler(Math.PI / 2, 0, 0);
      pos = new THREE.Vector3(0, radius, 0);
      map = gradTexture([[1], [hexColor]]);
      break;
    case 'bottom':
      rot = new THREE.Euler(-Math.PI / 2, 0, 0);
      pos = new THREE.Vector3(0, -radius, 0);
      map = gradTexture([[1], [floorColor]]);
      break;
    case 'back':
      rot = new THREE.Euler(0, 0, 0);
      pos = new THREE.Vector3(0, 0, -radius);
      map = gradientTexture;
      break;
    case 'front':
      rot = new THREE.Euler(0, Math.PI, 0);
      pos = new THREE.Vector3(0, 0, radius);
      map = gradientTexture;
      break;
    case 'left':
      rot = new THREE.Euler(0, Math.PI / 2, 0);
      pos = new THREE.Vector3(-radius, 0, 0);
      map = gradientTexture;
      break;
    case 'right':
      rot = new THREE.Euler(0, -Math.PI / 2, 0);
      pos = new THREE.Vector3(radius, 0, 0);
      map = gradientTexture;
      break;
    default:
      rot = new THREE.Euler(-Math.PI / 2, 0, 0);
      pos = new THREE.Vector3(0, 0, -1000);
      map = gradientTexture;
  }
  return (
    <mesh rotation={rot} position={pos}>
      <planeGeometry attach="geometry" args={[radius * 2, radius * 2]} />
      <meshBasicMaterial
        attach="material"
        map={map}
        side={THREE.FrontSide}
        depthWrite={false}
        fog={false}
      />
    </mesh>
  );
}

function gradTexture(color: Array<any>) {
  const c = document.createElement('canvas');
  const ct = c.getContext('2d');
  const size = 1024;
  c.width = 16;
  c.height = size;
  if (ct) {
    const gradient = ct.createLinearGradient(0, 0, 0, size);
    let i = color[0].length;
    while (i--) {
      gradient.addColorStop(color[0][i], color[1][i]);
    }
    ct.fillStyle = gradient;
    ct.fillRect(0, 0, 16, size);
  }
  const texture = new THREE.Texture(c);
  texture.needsUpdate = true;
  return texture;
}
